import {
  LOAD_LIBRARY,
  LoadLibraryAction, loadLibrarySuccess,
  CREATE_APPLICATION,
  CreateApplicationAction,
  createApplicationSuccess,
  UnsubscribeFromEmailsAction,
  UNSUSCRIBE_FROM_EMAILS,
  unsubscribeFromEmailsSuccess,
  SubmitPreQualificationAction,
  submitPreQualificationSuccess,
  SUBMIT_PRE_QUALIFICATION,
  setPreQualificationFlag,
  SubmitDealerApplicationAction,
  submitDealerApplicationSuccess,
  SUBMIT_DEALER_APPLICATION,
  LoadStatesAction,
  loadStatesSuccess,
  LOAD_STATES,
  LoadCustomerReferenceRelationshipsAction,
  loadCustomerReferenceRelationshipsSuccess,
  LOAD_CUSTOMER_REFERENCE_RELATIONSHIPS,
  UpdateApplicationAction,
  UPDATE_APPLICATION,
  CreateDownPaymentLinkAction,
  CREATE_DOWN_PAYMENT_LINK,
  createDownPaymentLinkSuccess,
  createDownPaymentLinkFailure
} from './actions';
import { put, takeLatest, all, call } from 'redux-saga/effects';
import { httpGet, httpPost, httpPut } from '../../Http/HttpClient';
import { LookupItems } from '../../Models/LookupItems';
import { displayError, isValidURL } from '../../Helpers/Utilities';
import { isNil } from 'ramda';
import { PreQualifyResult } from '../../Models/PreQualifyResult';
import { State } from '../../Models/State';
import { CustomerReferenceRelationship } from '../../Models/CustomerReferenceRelationship';
import {CreateApplicationFromFormResults} from "../../Models/CreateApplicationFromFormResults";
import {CuratedSiteDownPaymentRequestDetails} from "../../Models/CuratedSiteDownPaymentRequestDetails";

function* loadLibrarySaga(action: LoadLibraryAction) {
  try {
    const apiUrl = isNil(action.payload.locationId)
      ? `Lookup/creditApplication?clientId=${action.payload.clientId.toUpperCase()}`
      : `Lookup/creditApplication?clientId=${action.payload.clientId.toUpperCase()}&locationId=${action.payload.locationId!.toUpperCase()}`;
    const response: { ok: boolean, json: any, message?: any } = yield call(httpGet, apiUrl,);

    if (response && response.ok) {
      const lookupItems: LookupItems = yield call([response, response.json]);
      yield put(loadLibrarySuccess(lookupItems));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading library'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchLibrary() {
  yield takeLatest(LOAD_LIBRARY, loadLibrarySaga);
}

// Load States
function* loadStatesSaga(action: LoadStatesAction) {
  try {
    const response: { ok: boolean, json: any, message?: any } = yield call(httpGet, `Lookup/states?customerPortalLinkId=${action.payload.customerPortalLinkId}`);
    if (response && response.ok) {
      const states: State[] = yield call([response, response.json]);
      yield put(loadStatesSuccess(states));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An error occured when getting list of states.'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchStates() {
  yield takeLatest(LOAD_STATES, loadStatesSaga);
}

function* createApplicationSaga(action: CreateApplicationAction) {
  try {
    const apiUrl = isNil(action.payload.locationId)
      ? `Application/createFromOnlineForm?clientId=${action.payload.clientId.toUpperCase()}&useCuratedSite=${action.payload.useCuratedSite}`
      : `Application/createFromOnlineForm/${action.payload.locationId}?clientId=${action.payload.clientId.toUpperCase()}&useCuratedSite=${action.payload.useCuratedSite}`;

    const response: { json: any, ok: boolean } = yield call(httpPost,
      apiUrl,
      action.payload.application
    );

    // API will return either a redirect url or an error message.
    const result: { redirectUrl?: string } | { error?: string } = yield call([response, response.json]);
    if ((result as { error: string })?.error) {
      yield put(displayError(result as string));
    } else {
      // We will redirect to the custom url if one exists. Empty string indicates we need to redirect to the portal login page.
      yield put(createApplicationSuccess(result as CreateApplicationFromFormResults));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchCreateApplication() {
  yield takeLatest(CREATE_APPLICATION, createApplicationSaga);
}

// Update application
function* updateApplicationSaga(action: UpdateApplicationAction) {
  try {
    const apiUrl = action.payload.customerId
      ? `application/updateApplication/${action.payload.customerId}?clientId=${action.payload.clientId.toUpperCase()}&customerPortalLinkId=${action.payload.customerPortalLinkId}`
      : `application/updateApplication?clientId=${action.payload.clientId.toUpperCase()}&customerPortalLinkId=${action.payload.customerPortalLinkId}`;
    const response: { json: any, ok: boolean } = yield call(httpPut,
      apiUrl,
      action.payload.application
    );
    // API will return either a redirect url or an error message.
    const result: CreateApplicationFromFormResults | { error?: string } = yield call([response, response.json]);
    if (!response.ok || (result as { error: string })?.error) {
      yield put(displayError(response.json as string));
    } else {
      // We will redirect to the custom url if one exists. Empty string indicates we need to redirect to the portal login page.
      yield put(createApplicationSuccess(result as CreateApplicationFromFormResults));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchUpdateApplication() {
  yield takeLatest(UPDATE_APPLICATION, updateApplicationSaga);
}


function* unsubscribeFromEmailsSaga(action: UnsubscribeFromEmailsAction) {
  try {

    const response: { json: any } = yield call(httpPost,
      `Email/unsubscribe/${action.payload.emailAddress}/?clientId=${action.payload.clientId.toUpperCase()}`,
      action.payload,
    );
    const result: { message: string } = yield call([response, response.json]);
    if (result?.message) {
      yield put(unsubscribeFromEmailsSuccess(result.message));
    } else {
      yield put(displayError('Unexpected error occured while unsubscribing.'));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchUnsubscribeFromEmails() {
  yield takeLatest(UNSUSCRIBE_FROM_EMAILS, unsubscribeFromEmailsSaga);
}

function* submitPreQualifySaga(action: SubmitPreQualificationAction) {
  try {
    const apiUrl = isNil(action.payload.locationId)
      ? `Credit/preQualify?clientId=${action.payload.preQualifySubmissionInfo.clientId.toUpperCase()}`
      : `Credit/preQualify/${action.payload.locationId}?clientId=${action.payload.preQualifySubmissionInfo.clientId.toUpperCase()}`;
    const response: { json: any } = yield call(httpPost, apiUrl, action.payload.preQualifySubmissionInfo);
    const preQualifyResponse: { result: PreQualifyResult } = yield call([response, response.json]);
    if (preQualifyResponse && preQualifyResponse.result) {
      yield put(setPreQualificationFlag(false));
      yield put(submitPreQualificationSuccess(preQualifyResponse.result));
    } else {
      yield put(setPreQualificationFlag(false));
      yield put(displayError("Unable to complete pre-qualification request."));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchSubmitPreQualify() {
  yield takeLatest(SUBMIT_PRE_QUALIFICATION, submitPreQualifySaga);
}
/** End Client Lookups */

/** Dealer Application Form */
function* submitDealerApplicationFormSaga(action: SubmitDealerApplicationAction) {
  try {
    const response: { json: any } = yield call(httpPost, `Application/createFromDealerApp?clientId=${action.payload.clientId.toUpperCase()}`, action.payload.application);
    const result: Response | { error?: string } = yield call([response, response.json]);
    if ((result as { error: string })?.error) {
      yield put(displayError(result as string));
    } else {
      yield put(submitDealerApplicationSuccess());
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchSubmitDealerApplicationForm() {
  yield takeLatest(SUBMIT_DEALER_APPLICATION, submitDealerApplicationFormSaga);
}
/** End Dealer Application Form */

function* loadCustomerReferenceRelationshipsSaga(action: LoadCustomerReferenceRelationshipsAction) {
  try {
    const response: { ok: boolean, json: any, message?: any } = yield call(httpGet, `Lookup/customerReferenceRelationships?customerPortalLinkId=${action.payload.customerPortalLinkId}`);

    if (response && response.ok) {
      const customerReferenceRelationships: CustomerReferenceRelationship[] = yield call([response, response.json]);
      yield put(loadCustomerReferenceRelationshipsSuccess(customerReferenceRelationships));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading customer reference relationships.'));
      }
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchLoadCustomerReferenceRelationships() {
  yield takeLatest(LOAD_CUSTOMER_REFERENCE_RELATIONSHIPS, loadCustomerReferenceRelationshipsSaga);
}

// The createLink endpoint answers a refused request with a 400 whose body is the reason as plain text (or, with a JSON
// Accept header, as a JSON string), and an unexpected failure with problem details; this reads whichever came back.
const readErrorMessage = async (response: Response): Promise<string> => {
  const fallback = 'There was an error creating the deposit link.';
  try {
    const text = await response.text();
    if (!text) {
      return fallback;
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'string') {
        return parsed;
      }
      return parsed?.detail ?? parsed?.title ?? text;
    } catch {
      return text;
    }
  } catch {
    return fallback;
  }
};

// Create Down Payment Link
function* createDownPaymentLinkSaga(action: CreateDownPaymentLinkAction) {
  try {
    const response: Response | undefined = yield call(httpPost, `DownPayment/createLink?clientId=${action.payload.clientId.toUpperCase()}`, action.payload);
    if (!response) {
      yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      yield put(createDownPaymentLinkFailure());
    } else if (!response.ok) {
      const message: string = yield call(readErrorMessage, response);
      yield put(displayError(message));
      yield put(createDownPaymentLinkFailure());
    } else {
      // Ensure the link returns is a valid link. If not, display an error. A failed quote comes back with no link on
      // purpose: the deposit page shows the configured quote-failed message instead.
      const downPaymentLinkResult: CuratedSiteDownPaymentRequestDetails = yield call([response, response.json]);
      if (downPaymentLinkResult.quoteFailed || isValidURL(downPaymentLinkResult.link)) {
        yield put(createDownPaymentLinkSuccess(downPaymentLinkResult));
      } else {
        yield put(displayError(downPaymentLinkResult.link));
        yield put(createDownPaymentLinkFailure());
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
    yield put(createDownPaymentLinkFailure());
  }
}

export function* watchCreateDownPaymentLink() {
  yield takeLatest(CREATE_DOWN_PAYMENT_LINK, createDownPaymentLinkSaga);
}

export default function* sharedSagas() {
  yield all([
    watchLibrary(),
    watchCreateApplication(),
    watchUnsubscribeFromEmails(),
    watchSubmitPreQualify(),
    watchSubmitDealerApplicationForm(),
    watchStates(),
    watchLoadCustomerReferenceRelationships(),
    watchUpdateApplication(),
    watchCreateDownPaymentLink()
  ]);
}
